"use client"

import { useState, useEffect, useRef } from "react";
import ReactDOM from "react-dom"; 
import MarkdownIt from "markdown-it";
import markdownItAttrs from "markdown-it-attrs";
import pptxgen from "pptxgenjs";
import "./css/slide.css";
import ManualBarGraph from "../graph/ManualBarGraph";
import ManualLineGraph from "../graph/ManualLineGraph";
import ManualLineBarGraph from "../graph/ManualLineBarGraph";
import ManualMarketLineGraph from "../graph/ManualMarketLineGraph";
import api from "../api/api";
import DOMPurify from "dompurify";
import { modifyData } from "./graphUtils";
import html2canvas from "html2canvas";
import SlideComponent from "./SlideComponent";


// MarkdownIt のセットアップ
const mdParser = new MarkdownIt();
mdParser.use(markdownItAttrs);
mdParser.set({ html: true });

const Slide = ({ contents, showSlideProcessing }) => {
  const [markdown, setMarkdown] = useState(contents || "");
  const [currentSlide, setCurrentSlide] = useState(0);
  const slideRef = useRef(null);
  const [slides, setSlides] = useState([]);
  const [slidesData, setSlidesData] = useState([]);
  const [theme, setTheme] = useState("default");
  const graphCache = new Map(); 

  // スライドのパース処理
  const parseSlides = async (markdown) => {
    if (!markdown) return { slidesData: [], slides: [] };

    const lines = markdown.split("\n");
    let currentSlide = { title: "", content: [], isMainTitle: false };
    let lastSectionTitleProcessed = "";
    const slidesData = []; 
    for (let line of lines) {
      // line = line.trim();
      if (line.startsWith("# ")) {
        currentSlide = { title: line.replace("# ", ""), content: [], isMainTitle: true };
        // slidesData.push({ ...currentSlide });
        slidesData.push({ ...currentSlide });
        currentSlide = { title: "", content: [], isMainTitle: false };
      } else if (line.startsWith("## ")) {
        lastSectionTitleProcessed = line.replace("## ", "");
      } else if (line.startsWith("### ")) {
        if (currentSlide.content.length > 0) {
          // slidesData.push({ ...currentSlide });
          slidesData.push({ ...currentSlide });
        }
        
        const h3Line = line.replace("### ", "");
        const layoutMatch = h3Line.match(/\[(list|summary|graph|table-graph|table)\]/);
        if (layoutMatch) {
          currentSlide.layoutType = layoutMatch[1];
          const cleanTitle = line.replace(/\[(list|summary|graph|table-graph|table)\]/, "").trim();
          currentSlide = { title: lastSectionTitleProcessed, content: [cleanTitle], isMainTitle: false };
        } else {
          currentSlide.content.push(line);
        }
      } else if (line.startsWith("- ")) {
        currentSlide.content.push(line);
      } else {
        // currentSlide.content.push(line);
        const numberedListRegex = /\d+\.\s+(?:\*\*.*?\*\*[^0-9]*)/g;
        const matches = line.match(numberedListRegex);

        if (matches && matches.length > 0) {
          for (const match of matches) {
            currentSlide.content.push(match.trim());
          }
        } else {
          currentSlide.content.push(line);
        }
      }
    }
    // 最後の行の処理
    if (currentSlide.title || currentSlide.content.length > 0) {
      slidesData.push({ ...currentSlide }); 
    }
      
    if (showSlideProcessing) {
      setSlidesData((prev) => {
        if (prev.length === 0) {
          return slidesData;
        } else {
          const updatedData = [...prev];
          updatedData[updatedData.length - 1] = slidesData[slidesData.length - 1]; // 最後のスライドのみ上書き
          return updatedData;
        }
      });
    } else {
      setSlidesData(slidesData.filter(slide => slide.isMainTitle || slide.content.some(item => item !== "")));
    }
    return lastSectionTitleProcessed;
  };
    
  const makeSlideComponent = async () => {
    if (!slidesData || slidesData.length == 0) return;

    if (showSlideProcessing) { // streaming処理
      // **グラフを置換するのは最後のスライドのみ**
      const lastSlideIndex = slidesData.length - 1;
      if (lastSlideIndex >= 0) {
        const slideContent = slidesData[lastSlideIndex].content.join("\n");
        const processedContent = (
          <div
            dangerouslySetInnerHTML={{
              __html: DOMPurify.sanitize(mdParser.render(slideContent)),
            }}
          />
        )
        slidesData[lastSlideIndex].content = processedContent
      }

      const newSlide = (
        <SlideComponent
          key={lastSlideIndex}
          slideData={slidesData[lastSlideIndex]}
          content={slidesData[lastSlideIndex].content}
        />
      );

      setSlides((prev) => {
        const updated = [...prev];
        updated[lastSlideIndex] = newSlide;
        return updated;
      });

    } else {
      const slides = await Promise.all(
        slidesData.map(async (slideData, index) => {
          const processedContent = await replaceGraph(slideData.content.join("\n"));
          return (
            <SlideComponent
              key={index}
              slideData={slideData}
              content={processedContent}
            />
          );
        })
      );
      setSlides(slides)
    }
  }

  const getFigComponent = (figType, figIndex, figData, modifyDataFn, figWidth="80%", figHeight=350) => {
    if (figType === "bar") {
      return (
        <div
          id={`graph-group-${figIndex}`}
          data-slide-index={currentSlide}  // 現在のスライド番号を付与（必要に応じて適宜設定）
          className="graph-group flex flex-wrap gap-[1rem] justify-center"
          key={`group-${figIndex}`}
        >
          <ManualBarGraph
            key={`graph-${figIndex}`}
            data={modifyDataFn(figData)}
            showBrush={false}
            showLabel="visible"
            colorType="default"
            showXaxisName={false}
            figWidth={figWidth}
            height={figHeight}
          />
        </div>
      )
    } else if (figType === "line") {
      return (
        <div
          id={`graph-group-${figIndex}`}
          data-slide-index={currentSlide}  // 現在のスライド番号を付与（必要に応じて適宜設定）
          className="graph-group flex flex-wrap gap-[1rem] justify-center"
          key={`group-${figIndex}`}
        >
          <ManualLineGraph
              key={`graph-${figIndex}`}
              data={modifyDataFn(figData)}
              showBrush={false}
              showLabel="visible"
              colorType="default"
              showXaxisName={false}
              figWidth={figWidth}
              height={350}
            />
        </div>
      )
    } else if (figType === "line-bar") {
      return (
        <div
            id={`graph-group-${figIndex}`}
            data-slide-index={currentSlide}  // 現在のスライド番号を付与（必要に応じて適宜設定）
            className="graph-group flex flex-wrap gap-[1rem] justify-center"
            key={`group-${figIndex}`}
          >
          <ManualLineBarGraph
            key={`graph-${figIndex}`}
            data={modifyDataFn(figData)}
            showBrush={false}
            showLabel="visible"
            colorType="default"
            showXaxisName={false}
            figWidth={figWidth}
          />
        </div>
      )
    } else if (figType === "market-line") {
      return (
        <div
          id={`graph-group-${figIndex}`}
          data-slide-index={currentSlide}  // 現在のスライド番号を付与（必要に応じて適宜設定）
          className="graph-group flex flex-wrap gap-[1rem] justify-center"
          key={`group-${figIndex}`}
        >
          <ManualMarketLineGraph
            key={`graph-${figIndex}`}
            data={modifyDataFn(figData)}
            showBrush={false}
            colorType="default"
            showXaxisName={false}
            figWidth={figWidth}
            height={350}
          />
        </div>
      )
    }
  }

  const getFigType = (fig_data) => {
    const graphTypes = fig_data.map((fig) => fig.graphType);
    if (graphTypes.includes("bar") && graphTypes.includes("line")) {
      return "line-bar";
    } else if (graphTypes.includes("bar")) {
      return "bar";
    } else if (graphTypes.includes("line")) {
      return "line";
    } else {
      return graphTypes[0];
    }
  };

  // グラフプレースホルダーのレンダリング関数
  const renderMarkdownWithGraphs = (text, figures, modifyDataFn) => {
    if (!text) return [];
    const parts = text.split(/(@@GraphPlaceholder-\d+@@)/g);
    const result = [];
    let placeholderBuffer = [];

    parts.forEach((part, index) => {
      const match = part.match(/@@GraphPlaceholder-(\d+)@@/);
      if (match) {
        const figIndex = parseInt(match[1], 10);
        const figData = figures[figIndex]?.fig_data || [];
        const figType = getFigType(figData)

        let element = getFigComponent(figType, figIndex, figData, modifyDataFn)

        if (element) placeholderBuffer.push(element);
      } else {
        if (placeholderBuffer.length > 0) {
          result.push(<div key={`group-${index}`}>{placeholderBuffer}</div>);
          placeholderBuffer = [];
        }
        
        result.push(
          <span
            key={`text-${index}`}
            dangerouslySetInnerHTML={{
              __html: DOMPurify.sanitize(mdParser.render(part.replace(/<\/p>/g, '</p>\n'))),
            }}
          />
        );
      }
    });

    return result;
  };

  // グラフデータ取得用 API
  const graphDataCache = new Map(); // 外に出して使い回す
  const getGraphData = async (settings) => {
    if (graphDataCache.has(settings)) {
      return graphDataCache.get(settings);
    }
    try {
      const response = await api.post(
        `${process.env.REACT_APP_API_URL}/chat/api/report-graph/`,
        { settings }
      );
      graphDataCache.set(settings, response.data.fig_data);
      return response.data.fig_data;
    } catch (error) {
      console.error("APIエラー:", error);
      return null;
    }
  };


  // Markdown の [type=...] を解析してグラフに置換
  const replaceGraph = async (contents) => {
    console.log("replaceGraph")
    let updatedText = contents;
    const newFigures = [];
    const regex = /\[type=(.+?)\]/g;
    let match;
    let index = 0;

    while ((match = regex.exec(contents)) !== null) {
      const settings = match[0];
      const graphData = await getGraphData(settings);
      newFigures.push({
        fig_data: graphData,
        replace_string: settings,
      });
      updatedText = updatedText.replace(settings, `@@GraphPlaceholder-${index}@@`);
      index++;
    }

    return renderMarkdownWithGraphs(updatedText, newFigures, modifyData);
  };

  useEffect(() => {
    makeSlideComponent(slidesData)
  }, [slidesData]) 

  useEffect(() => {
    if (!markdown || !markdown.trim()) return; // null や undefined の場合も防ぐ
    
    const fetchSlides = async () => {
        try {
            if (showSlideProcessing) {
                const trimmedMarkdown = markdown.trim();
                if (markdown.endsWith("### ")) {
                  setSlidesData((prev) => [...prev, {}])
                }
                if (trimmedMarkdown.includes("### ")) {
                    // 最後の "### " を見つける
                    const lastIndex = trimmedMarkdown.lastIndexOf("### ");

                    // 最後の "### " 以降を取得（新しいスライドとして処理）
                    const newSlideMarkdown = trimmedMarkdown.substring(lastIndex).trim();

                    await parseSlides(newSlideMarkdown);
                } else {
                    await parseSlides(markdown);
                }
            } else {
                parseSlides(markdown);
            }
        } catch (error) {
            console.error("スライドの解析エラー:", error);
        }
    };

    fetchSlides(); // `useEffect` 内で `async` 関数を適切に呼び出す
  }, [markdown, showSlideProcessing]);


  useEffect(() => {
    setMarkdown(contents || "");
    if (showSlideProcessing) {
      setCurrentSlide(slides.length - 1);
    } else {
      setCurrentSlide(0);
    }
  }, [contents, slides.length]);

  const handlePrevSlide = () => {
    setCurrentSlide((current) => Math.max(0, current - 1));
  };

  const handleNextSlide = () => {
    setCurrentSlide((current) => Math.min(slides.length - 1, current + 1));
  };

  // **太字** を認識してpptxgenjsのtext runs形式に変換
  const parseBoldText = (inputText) => {
    const regex = /\*\*(.+?)\*\*/g;
    const segments = [];
    let lastIndex = 0;
    let match;

    while ((match = regex.exec(inputText)) !== null) {
      if (match.index > lastIndex) {
        segments.push({ text: inputText.substring(lastIndex, match.index) });
      }
      segments.push({ text: match[1], options: { bold: true } });
      lastIndex = regex.lastIndex;
    }

    if (lastIndex < inputText.length) {
      segments.push({ text: inputText.substring(lastIndex) });
    }
    return segments;
  };

  // テーマカラーの設定
  const getThemeColor = (theme) => {
    let masterBg, titleColor, contentColor, headerBg, altRowColor;
    switch (theme) {
      case "dark":
        masterBg = { color: "1A1A1A" };
        titleColor = "E0E0E0";
        contentColor = "E0E0E0";
        headerBg = "333333";
        altRowColor = "2A2A2A";
        break;
      case "gradient":
        masterBg = {
          color: "000428",
          type: "gradient",
          gradient: { type: "linear", color2: "004e92", angle: 45 },
        };
        titleColor = "FFFFFF";
        contentColor = "E0E0E0";
        headerBg = "0054A6";
        altRowColor = "E5F3FF";
        break;
      case "minimal":
        masterBg = { color: "FFFFFF" };
        titleColor = "333333";
        contentColor = "555555";
        headerBg = "F2F2F2";
        altRowColor = "F9F9F9";
        break;
      default:
        masterBg = { color: "FFFFFF" };
        titleColor = "333333";
        contentColor = "555555";
        headerBg = "F2F2F2";
        altRowColor = "F9F9F9";
    }
    return {
      masterBg: masterBg,
      titleColor: titleColor,
      contentColor: contentColor,
      headerBg: headerBg,
      altRowColor: altRowColor,
    }
  }

  // ダウンロード関数
  const downloadPowerPoint = async () => {
    const pptx = new pptxgen();
    pptx.layout = "LAYOUT_16x9";
  
    let titleFontSize, contentsTitleFontSize, fontSize;
    const reportType = 'bussiness'
    if (reportType === "presentation") {
      titleFontSize = 32
      contentsTitleFontSize = 20
      fontSize = 16
    } else {
      titleFontSize = 24
      contentsTitleFontSize = 20
      fontSize = 11
    }

    const {masterBg, titleColor, contentColor, headerBg, altRowColor} = getThemeColor(theme)
    
    let mainTitle;
    // 通常の for ループを利用して await を正しく利用する
    for (let i = 0; i < slidesData.length; i++) {
      const slide = slidesData[i];
      const pptSlide = pptx.addSlide();
      let startY = 22; // 初期Y座標(%)
      let currentY = startY;
        
      if (slide.isMainTitle) { // メインタイトルの処理
        mainTitle = slide.title;
        pptSlide.background = { color: "#40E0D0" };
        pptSlide.addShape(pptx.ShapeType.rect, {
          x: "0%",
          y: "20%",
          w: "100%",
          h: "40%",
          fill: { type: "solid", color: "#40E0D0" },
          line: { type: "none" },
        });
        pptSlide.addText(parseBoldText(mainTitle), {
          x: "5%",
          y: "30%",
          w: "90%",
          h: "40%",
          fontSize: titleFontSize,
          fontFace: "Meiryo",
          color: "FFFFFF",
          bold: true,
          align: "left",
        });
      } else { // メインタイトル以外の処理
        pptSlide.background = masterBg;
        // スライドタイトル（##）の処理
        const title = slide.title;
        if (title) {
          pptSlide.addShape(pptx.ShapeType.rect, {
            x: "0%",
            y: "0%",
            w: "100%",
            h: "10%",
            fill: { type: "solid", color: "#40E0D0" },
            line: { type: "none" },
          });
          pptSlide.addText(title, {
            x: "2.5%",
            y: "1%",
            w: "90%",
            h: "10%",
            fontSize: 24,
            fontFace: "Meiryo",
            color: "#FFFFFF",
            bold: true,
            align: "left",
          });
        }

        // コンテンツタイトルの処理
        // 1行目
        const contentsTitle =
          slide.content.length > 0
            ? slide.content[0].replace(/#+\s/g, "").trim()
            : "";
        if (contentsTitle) {
          pptSlide.addText(parseBoldText(contentsTitle), {
            x: "2.5%",
            y: "13%",
            w: "90%",
            h: "10%",
            fontSize: contentsTitleFontSize,
            fontFace: "Meiryo",
            color: contentColor,
            bold: true,
            align: "left",
          });
        }
  
        if (slide.content.length > 1) {
          // タイトル行以外の行を処理（必要に応じて slice() で除外）
          const lines = slide.content.slice(1); // もし最初の行が既に使われているなら
          let i = 0;
          while (i < lines.length) {
            const line = lines[i].trim();
            if (line === "") {
              i++;
              continue;
            }
            
            // 箇条書き（リスト）のブロック処理
            if (line.startsWith("- ")) {
              const bulletLines = [];
              while (i < lines.length && lines[i].trim().startsWith("- ")) {
                // "- " を除去し、<p> タグがあれば削除
                const text = lines[i].trim().substring(2).replace(/<\/?p>/g, "").trim();
                bulletLines.push(text);
                i++;
              }
              // 箇条書きブロックの各行を追加
              const baseLineHeight = 6; // 1行あたりの高さ(%)
              const maxWidth = 90;
              bulletLines.forEach((text) => {
                const estimatedLines = Math.ceil(text.length / 35);
                const textHeight = baseLineHeight * estimatedLines;
                pptSlide.addText(parseBoldText(text), 
                {
                  x: "5%",
                  y: `${currentY}%`,
                  w: `${maxWidth}%`,
                  h: `${textHeight}%`,
                  align: "left",
                  fontFace: "Meiryo",
                  fontSize: fontSize,
                  bullet: { code: "25CF" },
                });
                currentY += textHeight + 0.5;
              });
              continue; // 次のブロックへ
            }
            
            // テーブルブロックの処理（連続する | から始まる行）
            if (line.startsWith("|")) {
              const tableBlock = [];
              while (i < lines.length && lines[i].trim().startsWith("|")) {
                tableBlock.push(lines[i].trim());
                i++;
              }
              if (tableBlock.length >= 3) { // ヘッダー、区切り、最低1データ行があると仮定
                const headers = tableBlock[0]
                  .split("|")
                  .filter(Boolean)
                  .map((h) => h.trim());
                const dataRows = tableBlock.slice(2).map((row) =>
                  row.split("|").filter(Boolean).map((cell) => cell.trim())
                );

                const tableOpts = {
                  x: "5%",
                  y: `${currentY}%`,
                  w: "90%",
                  border: { type: "none" },
                  fontSize: fontSize,
                  fontFace: "Meiryo",
                };

                const rows = [];
                // ヘッダー行
                rows.push(
                  headers.map((header) => ({
                    text: parseBoldText(header),
                    options: {
                      bold: true,
                      fill: { color: headerBg },
                      color: titleColor,
                      fontFace: "Meiryo",
                      border: { type: "none" },
                    },
                  }))
                );

                // データ行
                dataRows.forEach((row, index) => {
                  rows.push(
                    row.map((cell) => {
                      const parsed = parseBoldText(cell); // ここが配列になる想定
                      if (Array.isArray(parsed)) {
                        const obj = parsed[0];  // 1つしか返さないなら先頭だけ取る
                        obj.options = {
                          ...obj.options,
                          fill: { color: index % 2 === 1 ? altRowColor : "FFFFFF" },
                          fontFace: "Meiryo",
                          border: { type: "none" },
                        };
                        return obj;
                      } else {
                        return {
                          text: parsed,
                          options: {
                            fill: { color: index % 2 === 1 ? altRowColor : "FFFFFF" },
                            fontFace: "Meiryo",
                            border: { type: "none" },
                          },
                        };
                      }
                    })
                  );
                });


                // テーブルを追加
                pptSlide.addTable(rows, tableOpts);

                // ✅ 高さ計算 (1行あたり 5% で計算)
                const rowHeightPercent = 6;
                const tableHeight = (dataRows.length + 1) * rowHeightPercent; // +1 はヘッダー
                currentY += tableHeight;
              }
              continue;
            }

            
            // グラフブロックの処理（[type=...] で始まる行）
            if (line.startsWith("[type=")) {
              // グラフ処理は1行単位でも可能ですが、必要なら連続するグラフ行をまとめてもよい
              const regex = /\[type=(.+?)\]/g;
              let match;
              while ((match = regex.exec(line)) !== null) {
                const settings = match[0];
                const graphData = await getGraphData(settings);
                const figIndex = parseInt(match[1], 10);
                const figData = graphData;
                const figType = graphData.length > 0 ? graphData[0].graphType : "bar";
                // ① グラフの JSX 要素を作成
                const element = getFigComponent(figType, 0, figData, modifyData);
                // ② 一時コンテナを作成してグラフをレンダリング、キャプチャ後にPPTに画像として追加
                const tempContainer = document.createElement("div");
                tempContainer.style.position = "fixed";
                tempContainer.style.top = "100vh";
                tempContainer.style.left = "0";
                tempContainer.style.width = "600px";
                tempContainer.style.height = "350px";
                document.body.appendChild(tempContainer);
                await new Promise((resolve) => {
                  ReactDOM.createRoot(tempContainer).render(element);
                  setTimeout(async () => {
                    const canvas = await html2canvas(tempContainer);
                    const imgData = canvas.toDataURL("image/png");
                    pptSlide.addImage({
                      data: imgData,
                      x: "25%",
                      y: `${currentY}%`,
                      w: "50%",
                      h: "50%",
                    });
                    currentY += 35;
                    ReactDOM.createRoot(tempContainer).unmount();
                    document.body.removeChild(tempContainer);
                    resolve();
                  }, 1000);
                });
              }
              i++;
              continue;
            }
            
            // 通常の段落として処理（リスト、テーブル、グラフのいずれにも該当しない行）
            // HTMLタグ（例：<p>）は除去しておく
            const paragraph = line.replace(/<\/?p>/g, "").trim();
            pptSlide.addText(parseBoldText(paragraph), {
              x: "5%",
              y: `${currentY}%`,
              w: "90%",
              h: "7%",
              align: "left",
              fontFace: "Meiryo",
              fontSize: fontSize,
              color: contentColor,
            });
            currentY += 6;
            i++;
          }
        }
      } 
    }
    pptx.writeFile({ fileName: `${mainTitle ? mainTitle : "財務分析"}.pptx` });
  };  

  return (
    <div className="app-container" ref={slideRef}>
      <div className="slide-container">
        <div className="slide-inner">
          {slides[currentSlide]} {/* ここで直接スライドをレンダリング */}
        </div>
        {!showSlideProcessing && contents && 
          <div className="slide-controls">
          <button onClick={handlePrevSlide} disabled={currentSlide === 0}>
            前へ
          </button>
          <span>
            {currentSlide + 1} / {slides.length}
          </span>
          <button onClick={handleNextSlide} disabled={currentSlide === slides.length - 1}>
            次へ
          </button>
          <button onClick={downloadPowerPoint} className="download-btn">
            PowerPointをダウンロード
          </button>
        </div>
        }
      </div>
    </div>
  );
};

export default Slide;
